import React, {useContext, useEffect, useState} from 'react';
import {Alert, AlertTitle, Button, IconButton, InputAdornment, Link, Typography, useTheme,} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {useForm} from 'react-hook-form';
import {AutocompleteElement, FormContainer, TextFieldElement} from 'react-hook-form-mui';
import {useNavigate} from 'react-router-dom';
import {useMutation} from "@tanstack/react-query";

import PhoneMask from '@components/common/PhoneMask';
import RadioButtonGroup from '@components/common/HookFormMui/RadioButtonGroup';
import RequiredFields from "@components/common/HookFormMui/RequiredFields";
import UserContext from '@lib/contexts/userContext';
import {counties, states, userOptions} from '@lib/constants';
import Grid from '@mui/material/Unstable_Grid2';
import {PageContext} from '@lib/contexts/pageContext';
import {scrollToAlert, toOptions} from '@lib/utils';
import {API_URLS, axiosRequest} from "@lib/axios";

const SignUp = () => {
  const { setPageMessage, setPageErrorMessage } = useContext(PageContext);
  const { isAdmin, isLoggedIn } = useContext(UserContext);

  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [completed, setCompleted] = useState(false);

  const theme = useTheme();

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
  });

  const healthyTextQuestion = watch('program-eligibility');
  const allowEnewsletterQuestion = watch('allow_enews_question');

  const stateValue = watch('state', null);

  useEffect(() => {
    // When state is Illinois we switch to a dropdown list of counties.
    // If the county is not in that list, then clear it to force the user to update county.
    if (stateValue?.id === 'IL') {
      const county = getValues('county');
      if (!counties.includes(county)) {
        setValue('county', '');
      }
    }
  }, [getValues, setValue, stateValue]);

  useEffect(() => {
    if (healthyTextQuestion === 'yes') {
      setValue('allow_text_program', null);
    }
  }, [healthyTextQuestion, setValue]);

  useEffect(() => {
    if (allowEnewsletterQuestion === 'yes') {
      setValue('allow_enews', null);
    }
  }, [allowEnewsletterQuestion, setValue]);

  useEffect(() => {
    if (isLoggedIn()) {
      navigate(isAdmin() ? '/member/profile' : '/member/activities');
    }
  }, [isAdmin, isLoggedIn, navigate]);

  const signUpMutation = useMutation({
    mutationFn: async (data) => axiosRequest({
      method: 'POST',
      url: `${API_URLS.AUTH}/signup`,
      data,
    }),
    onSuccess: async () => {
      setCompleted(true);
      setPageMessage('A verification email has been sent to your email address. Please check your email and follow the instructions to verify your account.');
      scrollToAlert();
    },
    onError: async (err) => {
      console.error('[SignUp] Error creating account:', err);
      err.errors.forEach(e => {
        if (e.includes('Email address')) {
          setError('email', { type: 'unique', message: e });
        }
        if (e.includes('password')) {
          setError('password', { message: e });
        }
      });
      setPageErrorMessage(err);
      scrollToAlert();
    },
    onSettled: () => {
      setSubmitting(false);
    }
  });

  const submit = async (values) => {
    if (!values.phone_number.match(/[0-9]{10}/)) {
      return false;
    }
    setPageErrorMessage(null);
    setSubmitting(true);

    const input = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      switch (key) {
        case 'email':
        case 'first_name':
        case 'last_name':
        case 'city':
        case 'zip_code':
          acc[key] = value ? value.trim() : value;
          break;
        case 'phone_number': {
          // Phone number must be formatted with country code, ie: '+15551234567'
          let phoneDigits = value.replaceAll(/[^0-9]/g, '');
          if (phoneDigits && phoneDigits.length === 10) {
            phoneDigits = `+1${phoneDigits}`;
          }
          acc[key] = phoneDigits;
          break;
        }
        case 'county':
        case 'state':
        case 'age_range':
        case 'race':
        case 'ethnicity':
        case 'gender':
        case 'household_size':
          acc[key] = value?.id || value;
          break;
        case 'is_eligible':
          acc[key] = values[key] === 'true';
          break;
        case 'password':
          acc[key] = value;
          break;
        default: break;
      }
      return acc;
    }, {});

    signUpMutation.mutate(input);
    return true;
  };

  return (
    <Grid container gap={3} wrap={'nowrap'} columns={10}>
      <Grid xs={10} sm={10} md={10} lg={10}>
        {!completed && (
          <FormContainer onSuccess={handleSubmit(submit)} FormProps={{ className: 'mui' }}>
            <RequiredFields />
            <Typography variant="body1" className={'form-text orange'} sx={{fontWeight: 600, mb: '15px'}}>
              Participant information will not be used, shared, or sold outside of the Eat.Move.Save. program.
            </Typography>
            <Grid container spacing={2}>
              <Grid xs={12} sm={10} md={8}>
                <TextFieldElement
                  name="first_name"
                  id="field-first_name"
                  label={'First Name'}
                  control={control}
                  required
                  fullWidth
                />
              </Grid>
              <Grid xs={12} sm={10} md={8}>
                <TextFieldElement
                  name="last_name"
                  id="field-last_name"
                  label={'Last Name'}
                  control={control}
                  required
                  fullWidth
                />
              </Grid>

              {/* Note: We don't collect street_address here, per client request. */}

              <Grid xs={12} sm={10} md={8}>
                <TextFieldElement
                  id="field-city"
                  name="city"
                  label={'City/Town'}
                  control={control}
                  required
                  fullWidth
                />
              </Grid>
              <Grid xs={12} sm={7} md={6}>
                <AutocompleteElement
                  id="field-state"
                  name="state"
                  label="State"
                  control={control}
                  required
                  fullWidth
                  options={states.map(item => ({id: item.abbreviation, label: item.name}))}
                />
              </Grid>
              <Grid xs={12} sm={3} md={2}>
                <TextFieldElement
                  id="field-zip"
                  name="zip_code"
                  label={'Zip Code'}
                  control={control}
                  required
                  fullWidth
                />
              </Grid>
              <Grid xs={12} sm={10} md={8}>
                {stateValue?.id === 'IL' ? (
                  <AutocompleteElement
                    name="county"
                    label="County"
                    control={control}
                    required
                    fullWidth
                    options={toOptions(counties)}
                    autocompleteProps={{
                      id: 'field-county',
                    }}
                  />
                ) : (
                  <TextFieldElement
                    id="field-county"
                    name="county"
                    label={'County'}
                    control={control}
                    required
                    fullWidth
                  />
                )}
              </Grid>
              <Grid xs={12} sm={10} md={8}>
                <TextFieldElement
                  id="field-email"
                  name="email"
                  type={'email'}
                  label={'Email'}
                  control={control}
                  required
                  fullWidth
                  onChange={() => {
                    if (errors?.email?.unique) {
                      clearErrors('email');
                    }
                  }}
                />
              </Grid>
              <Grid xs={12} sm={10} md={8}>
                <TextFieldElement
                  id="field-phone"
                  name="phone_number"
                  type={'text'}
                  label={'Cell Phone Number'}
                  control={control}
                  validation={{
                    validate: {
                      tenDigits: (value) => value.replaceAll(/[^0-9]+/g, '').length === 10 || 'Phone number must be 10 digits',
                    },
                  }}
                  InputProps={{
                    inputComponent: PhoneMask,
                  }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid xs={10} id="field-is_eligible">
                <RadioButtonGroup
                  name={'is_eligible'}
                  label={'Are you eligible to participate in any of the following programs?'}
                  formLabelProps={{
                    focused: false,
                  }}
                  row={true}
                  description={(error) => (
                    <ul style={{
                      listStyleType: 'none',
                      columns: 2,
                      paddingLeft: 0,
                      margin: 0,
                      color: error ? theme.palette.error.main : '#000'
                    }}>
                      <li>SNAP/LINK</li>
                      <li>WIC</li>
                      <li>Free or reduced-price school lunch or breakfast</li>
                      <li>Free summer meals</li>
                      <li>Medicaid</li>
                      <li>Head start</li>
                      <li>Food pantries</li>
                      <li>Affordable housing or shelters</li>
                    </ul>
                  )}
                  helperTextProps={{
                    sx: {
                      marginRight: 0,
                      marginLeft: 0,
                      marginTop: 0,
                      color: '#000',
                    },
                  }}
                  options={[
                    { id: 'true', label: 'Yes' },
                    { id: 'false', label: 'No' },
                  ]}
                  control={control}
                  required
                />
              </Grid>
              <Grid xs={10} id="field-program_eligibility">
                <RadioButtonGroup
                  name={'program-eligibility'}
                  label={'Do you participate in the Eat.Move.Save. Healthy Text Program?'}
                  formLabelProps={{
                    focused: false,
                  }}
                  row={true}
                  description={() => 'In this program participants get 1-2 messages on their cell phone each week with fun, ' +
                    'healthy tips for you and your family! Messages may also include upcoming free or low-cost events and classes.'}
                  helperTextProps={{
                    sx: {
                      marginRight: 0,
                      marginLeft: 0,
                      marginTop: 0,
                      color: '#000',
                    },
                  }}
                  options={[
                    { id: 'yes', label: 'Yes' },
                    { id: 'no', label: 'No' },
                    { id: 'unsure', label: 'Unsure' },
                  ]}
                  control={control}
                />
              </Grid>
              {(healthyTextQuestion === 'no' || healthyTextQuestion === 'unsure') &&
                <Grid xs={10} id="field-text_program">
                  <RadioButtonGroup
                    name={'allow_text_program'}
                    label={
                      <>
                        <span>Would you like to sign up for the Eat.Move.Save. Healthy Text Program?</span>
                        <span style={{ color: 'green', marginLeft: '5px', fontWeight: 600 }}>25 Points</span>
                      </>
                    }
                    formLabelProps={{
                      focused: false,
                    }}
                    row={true}
                    options={[
                      { id: 'true', label: 'Yes' },
                      { id: 'false', label: 'No' },
                    ]}
                    control={control}
                  />
                </Grid>
              }
              <Grid xs={10} id="field-enews_question">
                <RadioButtonGroup
                  name={'allow_enews_question'}
                  label={'Do you receive the Eat.Move.Save. Healthy eNewsletter each month?'}
                  formLabelProps={{
                    focused: false,
                  }}
                  row={true}
                  description={() =>
                    <p>
                      This newsletter comes from the University of Illinois-Illinois Nutrition Education Programs
                      (<span
                      style={{ color: 'red' }}>eatmovesave@illinois.edu</span>)
                    </p>
                  }
                  helperTextProps={{
                    sx: {
                      marginRight: 0,
                      marginLeft: 0,
                      marginTop: 0,
                      color: '#000',
                    },
                  }}
                  options={[
                    { id: 'yes', label: 'Yes' },
                    { id: 'no', label: 'No' },
                    { id: 'unsure', label: 'Unsure' },
                  ]}
                  control={control}
                />
              </Grid>
              {(allowEnewsletterQuestion === 'no' || allowEnewsletterQuestion === 'unsure') &&
                <Grid xs={10} id="field-allow_enews">
                  <RadioButtonGroup
                    name={'allow_enews'}
                    label={
                      <>
                        <span>Would you like to sign up for the Eat.Move.Save. Healthy eNewsletter Program?</span>
                        <span style={{ color: 'green', marginLeft: '5px', fontWeight: 600 }}>10 Points</span>
                      </>
                    }
                    formLabelProps={{
                      focused: false,
                    }}
                    row={true}
                    options={[
                      { id: 'true', label: 'Yes' },
                      { id: 'false', label: 'No' },
                    ]}
                    control={control}
                  />
                </Grid>
              }
              <Grid xs={12} sm={10} md={8}>
                <AutocompleteElement
                  name="household_size"
                  label={'Household Size'}
                  control={control}
                  fullWidth
                  options={toOptions(userOptions.householdSize)}
                  autocompleteProps={{
                    id: 'field-household_size',
                  }}
                />
              </Grid>
              <Grid xs={12} sm={10} md={8}>
                <AutocompleteElement
                  id="field-gender"
                  name="gender"
                  label={'Gender'}
                  control={control}
                  fullWidth
                  options={toOptions(userOptions.gender)}
                  autocompleteProps={{
                    id: 'field-gender',
                  }}
                />
              </Grid>
              <Grid xs={12} sm={10} md={8}>
                <AutocompleteElement
                  id="field-ethnicity"
                  name="ethnicity"
                  label={'Ethnicity'}
                  control={control}
                  fullWidth
                  options={toOptions(userOptions.ethnicity)}
                  autocompleteProps={{
                    id: 'field-ethnicity',
                  }}
                />
              </Grid>
              <Grid xs={12} sm={10} md={8}>
                <AutocompleteElement
                  id="field-race"
                  name="race"
                  label={'Race'}
                  control={control}
                  fullWidth
                  options={toOptions(userOptions.race)}
                  autocompleteProps={{
                    id: 'field-race',
                  }}
                />
              </Grid>
              <Grid xs={12} sm={10} md={8}>
                <AutocompleteElement
                  id="field-age_range"
                  name="age_range"
                  label={'Age Range'}
                  control={control}
                  fullWidth
                  options={toOptions(userOptions.ageRange)}
                  autocompleteProps={{
                    id: 'field-age_range',
                  }}
                />
              </Grid>
              <Grid xs={12} sm={10} md={8} sx={{ marginBottom: '2rem' }}>
                <TextFieldElement
                  id="field-password"
                  name="password"
                  label={'Create Password'}
                  helperText={'Minimum 10 characters. You can use letters, numbers, or symbols.'}
                  control={control}
                  required
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  validation={{
                    minLength: {
                      value: 10,
                      message: 'Password must have at least 10 characters'
                    }
                  }}
                  InputProps={{
                    endAdornment:
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          // onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff/> : <Visibility/>}
                        </IconButton>
                      </InputAdornment>
                  }
                  }
                />
              </Grid>
              {(!stateValue?.id || stateValue?.id !== 'IL') &&
                <Grid xs={12} sm={10} md={8}>
                  <Alert variant="outlined" severity="info" sx={{ marginBottom: '24px' }}>
                    <AlertTitle sx={{ color: 'ilblue.main' }}>
                      Notice for Non-Illinois Residents
                    </AlertTitle>
                    <Typography variant="body1" sx={{ color: '#000' }}>
                      myEat.Move.Save. is part of the Illinois SNAP-Education program.
                      Only Illinois residents are eligible to receive incentives from myEat.Move.Save.
                      Check with your state’s <Link target="_blank"
                                                 href="https://snaped.fns.usda.gov/state-snap-ed-programs"
                                                 rel="noreferrer">SNAP-Education
                      program</Link> for programs in your state.
                    </Typography>
                  </Alert>
                </Grid>
              }
              <Grid xs={12} sm={10} md={8}>
                <Typography>
                  After clicking SUBMIT you will receive a verification email to the address entered above.
                  Please follow the instructions in the email to activate your account.
                </Typography>
              </Grid>
              <Grid xs={12} sm={10} md={8}>
                <Button
                  disabled={submitting}
                  type={'submit'}
                  variant="contained"
                  size="small"
                  id="button-submit"
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </FormContainer>
        )}
      </Grid>
    </Grid>
  );
};

export default SignUp;
